import React from 'react';
import { Mail, Shield, Target, Eye } from 'lucide-react';
import { motion } from 'framer-motion';

const teamMembers = [
  { name: 'Khushi Chauhan',  role: 'Hosting & Deploy Manager', email: '2401201167@krmu.edu.in', image: '/images/team/khushi.png' },
  { name: 'Shweta Jha',      role: 'Backend Developer',        email: '2401201127@krmu.edu.in', image: '/images/team/shweta.png' },
  { name: 'Kartik Malhotra', role: 'Frontend Developer',       email: '2401201160@krmu.edu.in', image: '/images/team/kartik.png' },
  { name: 'Akanksha Kumari', role: 'Database Manager',         email: '2401201162@krmu.edu.in', image: '/images/team/akanksha.png' },
  { name: 'Vikram Das',      role: 'UI/UX Designer',           email: '2401201217@krmu.edu.in', image: '/images/team/vikram.png' },
];

const About = () => {
  return (
    <div className="min-h-screen font-sans text-white pt-24" style={{ 
      backgroundImage: "url('/images/team/team.png')",
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed',
      '--foreground': '#ffffff',
      '--muted-foreground': '#ffffff',
      '--card-foreground': '#ffffff',
      '--border': 'rgba(255, 255, 255, 0.15)',
      '--accent': 'rgba(255, 255, 255, 0.08)',
      '--muted': 'rgba(255, 255, 255, 0.05)',
    }}>

      {/* ── OUR TEAM (Now becomes the main entry section) ── */}
      <section className="py-16 border-b animate-fadeUp animate-once" style={{ 
        background: 'rgba(255,255,255,0.04)', 
        backdropFilter: 'blur(16px)', 
        WebkitBackdropFilter: 'blur(16px)',
        borderColor: 'rgba(255,255,255,0.1)'
      }}>
        <div className="section-container">
          <div className="text-center mb-16">
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight mb-4" style={{ color: '#ffffff' }}>
              The Team Behind MattersUrSkills
            </h1>
            <p className="text-sm sm:text-base max-w-2xl mx-auto leading-relaxed" style={{ color: '#ffffff' }}>
              A passionate group of students building next-generation technology to empower India's skilled workforce.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {teamMembers.map((member, i) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="rounded-2xl border p-6 text-center glass-panel highlight-border hover:shadow-2xl transition-all duration-300 group hover:-translate-y-1.5 cursor-pointer"
                style={{ 
                  background: 'rgba(255,255,255,0.08)', 
                  backdropFilter: 'blur(16px)', 
                  WebkitBackdropFilter: 'blur(16px)',
                  borderColor: 'rgba(255,255,255,0.15)'
                }}
                onClick={() => window.open(`mailto:${member.email}`)}
              >

                <h3 className="font-bold text-xs tracking-tight mb-1 group-hover:text-purple-400 transition-colors" style={{ color: '#ffffff' }}>{member.name}</h3>
                <p className="text-[9px] font-bold px-2.5 py-0.5 rounded-full border inline-block mb-4"
                  style={{ background: 'var(--muted)', color: '#ffffff', borderColor: 'rgba(255,255,255,0.15)' }}>
                  {member.role}
                </p>

                <div className="flex items-center justify-center gap-1.5 text-[10px] font-semibold transition-colors group-hover:text-purple-400"
                  style={{ color: '#ffffff' }}>
                  <Mail className="w-3 h-3 flex-shrink-0" />
                  <span className="truncate max-w-[120px]">{member.email}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── OUR PURPOSE ── */}
      <section className="py-24" style={{ 
        background: 'rgba(255,255,255,0.06)', 
        backdropFilter: 'blur(16px)', 
        WebkitBackdropFilter: 'blur(16px)',
        borderColor: 'rgba(255,255,255,0.1)'
      }}>
        <div className="section-container">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-3" style={{ color: '#ffffff' }}>Our Core Purpose</h2>
            <p className="text-xs sm:text-sm" style={{ color: '#ffffff' }}>The principles that drive our platform forward</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <Target className="w-5 h-5" />,
                title: 'Our Mission',
                text: "To connect India's skilled workforce with genuine work opportunities, eliminating fake job scams and predatory middlemen."
              },
              {
                icon: <Eye className="w-5 h-5" />,
                title: 'Our Vision',
                text: 'A future where every skilled individual has equal access to work opportunities, regardless of background or location.'
              },
              {
                icon: <Shield className="w-5 h-5" />,
                title: 'Our Values',
                text: 'Transparency, trust, and technology — three pillars that guide every product and business decision we make.'
              },
            ].map(({ icon, title, text }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="rounded-2xl border p-8 glass-panel highlight-border transition-all duration-300 hover:scale-[1.02] cursor-pointer"
                style={{ 
                  background: 'rgba(255,255,255,0.08)', 
                  backdropFilter: 'blur(16px)', 
                  WebkitBackdropFilter: 'blur(16px)',
                  borderColor: 'rgba(255,255,255,0.15)'
                }}
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-6"
                  style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}>
                  {icon}
                </div>
                <h3 className="font-bold text-base mb-3" style={{ color: '#ffffff' }}>{title}</h3>
                <p className="text-xs leading-relaxed" style={{ color: '#ffffff' }}>{text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
